import { ConsoleLogger } from '../internal/ConsoleLogger';
import { IsProduction } from './EnvProcess';

/** Same singleton the package exports as `Logger`; GetInstance is idempotent so this resolves to the one shared logger rather than a second instance. */
const logger = ConsoleLogger.GetInstance(!IsProduction);

/** Options describing one non-overlapping recurring background drain. */
export interface IRecurringWorkerOptions {
  /** Log context label for start and failure lines. */
  logContext: string;
  /** Fixed interval between ticks, in milliseconds. */
  intervalMs: number;
  /**
   * The work performed each tick. The resolved value is awaited then discarded (drains commonly return a processed count), so any promise is accepted. Rejections are caught, logged, and
   * swallowed so the next tick still runs.
   */
  task: () => Promise<unknown>;
  /** Optional one-time line logged when the worker starts. */
  startMessage?: string;
  /** Prefix prepended to the caught error message when a tick rejects. */
  failureMessage: string;
}

/**
 * Starts a periodic background drain with a non-overlapping `running` guard: a tick is skipped while the previous one is still in flight, so a slow pass or large backlog never stacks concurrent
 * runs. A rejected task is logged via the shared logger and swallowed so the timer keeps ticking; per-item isolation is the task's own responsibility.
 *
 * Replaces the previously copy-pasted `running`-guard + `setInterval` + `.catch().finally()` structure in the voice/billing reconcile and report workers.
 *
 * @param options - The worker configuration.
 * @returns The interval handle, so the caller can clear it on shutdown.
 */
export function StartRecurringWorker(options: IRecurringWorkerOptions): ReturnType<typeof setInterval> {
  let running = false;

  const handle = setInterval(() => {
    if (running) return;
    running = true;
    void options
      .task()
      .catch((err: unknown) => logger.Error(options.logContext, `${options.failureMessage}${logger.GetErrorMessage(err)}`))
      .finally(() => {
        running = false;
      });
  }, options.intervalMs);

  if (options.startMessage) logger.Log(options.logContext, options.startMessage);
  return handle;
}
